import React, { useCallback, useEffect, useState, useContext } from 'react'
import Head from 'next/head'
import { VenueCustomerLayout } from '../../components/old/Layout'
import { User, Event, Cart } from 'app/types/authorization'
import { Addon } from 'app/types/items'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import {
  FailureNotification,
  SuccessNotification,
} from '../../components/old/Notification'
import {
  Arena as ArenaType,
  ArenaSection,
  ArenaSectionSeatTypes,
} from 'app/types/arena'
import { StandardAPIRequest } from 'app/types/api'
import { clientGetIdToken, clientUserFromCognito } from 'app/utils/cognito'
import { getArenaForEvent, getEvent, getSections } from 'app/request/events'
import {
  getOrganization,
  getPublicCompanyDataFromOrganization,
} from 'app/request/organization'
import {
  CompanyData,
  CompanyProfile,
  Organization,
} from 'app/types/organization'
import LivedayLoader from '../../components/old/animations/LivedayLoader'
import { AuthContext } from 'app/utils/context'
import { captureException } from '@sentry/nextjs'
import { Arena } from '../../components/Arena'
import { Items } from '../../components/old/Items'
import { CheckoutCart } from '../../components/Cart'
import Currency from 'app/types/currency'

export default function EventPage() {
  const { state } = useContext(AuthContext)
  const { t } = useTranslation('common')
  const router = useRouter()
  const { eid, selected_section, email, phone_number, name } = router.query
  const [initialLoading, setInitialLoading] = useState<boolean>(true)
  const [user, setUser] = useState<User>()

  const [event, setEvent] = useState<Event | null>(null)
  const [arena, setArena] = useState<ArenaType | null>(null)
  const [sections, setSections] = useState<ArenaSection[]>([])
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(
    null
  )
  const [companyData, setCompanyData] = useState<CompanyData[] | null>([])
  const [organization, setOrganization] = useState<Organization | null>(null)

  const initialLoad = useCallback(async () => {
    try {
      if (eid === undefined || eid.length === 0) {
        return
      }
      const [user, event, arena, sections] = await Promise.all([
        clientUserFromCognito(),
        getEvent(eid),
        getArenaForEvent(eid),
        getSections(eid),
      ])
      setUser(user)
      setEvent(event)
      setArena(arena)
      setSections(sections)
      if (event === null) {
        return
      }
      if (event.organization) {
        const [organization, companyData] = await Promise.all([
          getOrganization(event.organization),
          getPublicCompanyDataFromOrganization(event.organization),
        ])
        setCompanyProfile(organization?.company_profile ?? null)
        setCompanyData(companyData)
        setOrganization(organization)
      }
    } catch (err) {
      captureException(err)
    } finally {
      setInitialLoading(false)
    }
  }, [eid])

  useEffect(() => {
    initialLoad()
  }, [initialLoad])

  const [currentStep, setCurrentStep] = useState<number>(1)
  const [shouldHaveCart, setShouldHaveCart] = useState(true)
  const [showFailureMessage, setShowFailureMessage] = useState<string>('')
  const [showSuccessMessage, setShowSuccessMessage] = useState<string>('')
  const [items, setItems] = useState<Addon[]>([])

  const shouldHaveTickets = useCallback(() => {
    return arena !== null
  }, [arena])

  const shouldHaveMerch = useCallback(() => {
    let shouldHaveItems = false
    items.forEach((item) => {
      if (item.product.Item !== undefined) {
        shouldHaveItems = true
      }
    })
    return shouldHaveItems
  }, [items])

  const shouldHaveKiosk = useCallback(() => {
    let shouldHaveItems = false
    items.forEach((item) => {
      if (item.product.Kiosk !== undefined) {
        shouldHaveItems = true
      }
    })
    return shouldHaveItems
  }, [items])

  const shouldHaveRestaurant = useCallback(() => {
    let shouldHaveItems = false
    items.forEach((item) => {
      if (item.product.Restaurant !== undefined) {
        shouldHaveItems = true
      }
    })
    return shouldHaveItems
  }, [items])

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => setShowSuccessMessage(''), 2500)
      return () => clearTimeout(timer)
    }
  }, [showSuccessMessage])

  useEffect(() => {
    if (showFailureMessage) {
      const timer = setTimeout(() => setShowFailureMessage(''), 2500)
      return () => clearTimeout(timer)
    }
  }, [showFailureMessage])

  const fetchData = useCallback(async () => {
    if (event?.sk !== undefined) {
      try {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/addon/event/${event.sk}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        const ticketData: StandardAPIRequest<Addon[]> = await response.json()
        if (ticketData.error) {
          throw new Error(ticketData.error.message)
        }
        setItems(ticketData.data ?? [])
      } catch (err) {
        captureException(err)
      }
    }
  }, [event?.sk])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const [cart, setCart] = useState<Cart>()
  const [ticketsInCart, setTicketsInCart] = useState<string[]>([])

  useEffect(() => {
    setTicketsInCart(
      (cart?.items ?? [])
        .filter((item) => item.Ticket !== undefined)
        ?.map((item) => item?.Ticket?.sk ?? '')
    )
  }, [cart?.items])

  const fetchCartData = useCallback(async () => {
    if (event?.sk !== undefined && t !== undefined) {
      const idToken = await clientGetIdToken()
      let headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Cart-Token': state.cartToken,
      }
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`
      }
      try {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/checkout/${event?.sk}/cart`,
          {
            headers,
          }
        )
        const cartData: StandardAPIRequest<Cart | null> = await response.json()
        if (cartData.error) {
          throw new Error(cartData.error.message)
        }
        if (cartData.data) {
          setCart(cartData?.data)
        }
      } catch (err) {
        captureException(err)
        setShowFailureMessage(t('unable-to-fetch-cart-items') ?? '')
      }
    }
  }, [event?.sk, state.cartToken, t])

  useEffect(() => {
    fetchCartData()
  }, [fetchCartData])

  useEffect(() => {
    let count = 1
    if (shouldHaveTickets()) {
      count += 1
    }
    if (shouldHaveMerch()) {
      count += 1
    }
    if (shouldHaveKiosk()) {
      count += 1
    }
    if (shouldHaveRestaurant()) {
      count += 1
    }
    setShouldHaveCart(currentStep !== count)
  }, [
    currentStep,
    shouldHaveTickets,
    shouldHaveKiosk,
    shouldHaveMerch,
    shouldHaveRestaurant,
  ])

  useEffect(() => {
    if (
      companyProfile?.font?.name !== undefined &&
      companyProfile?.font?.link !== undefined
    ) {
      const myFont = new FontFace(
        companyProfile.font.name,
        `url(${process.env.NEXT_PUBLIC_STATIC_URL}/${companyProfile.font.link})`
      )
      myFont.load().then((font) => {
        /* @ts-ignore */
        document.fonts.add(font)
      })
    }
  }, [companyProfile])

  const [sectionLoading, setSectionLoading] = useState<boolean>(false)
  const [tickets, setTickets] = useState<ArenaSectionSeatTypes[][]>([])
  const [selectedSection, setSelectedSection] = useState<ArenaSection>()

  useEffect(() => {
    if (selected_section !== undefined && sections.length > 0) {
      const selectedSection = sections.find(
        (section) => section.uuid === selected_section
      )
      if (selectedSection !== undefined) {
        setSelectedSection(selectedSection)
      }
    }
  }, [selected_section, sections])

  useEffect(() => {
    if (selectedSection === undefined) {
      setTickets([])
    }
  }, [selectedSection])

  const fetchTickets = useCallback(async () => {
    try {
      if (event !== null && selectedSection !== undefined) {
        setSectionLoading(true)
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/ticket/event/${event.sk}/${selectedSection.uuid}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Cart-Token': state.cartToken,
            },
          }
        )
        const ticketData: StandardAPIRequest<ArenaSectionSeatTypes[][]> =
          await response.json()
        if (ticketData.error) {
          throw new Error(ticketData.error.message)
        }
        setTickets(ticketData.data ?? [])
        setSectionLoading(false)
      }
    } catch (err) {
      captureException(err)
    }
  }, [event, selectedSection, state.cartToken])

  useEffect(() => {
    fetchTickets()
  }, [fetchTickets])

  const [totalSteps, setTotalSteps] = useState<number>(0)

  useEffect(() => {
    let count = 1
    if (shouldHaveTickets()) {
      count += 1
    }
    if (shouldHaveMerch()) {
      count += 1
    }
    if (shouldHaveKiosk()) {
      count += 1
    }
    if (shouldHaveRestaurant()) {
      count += 1
    }
    setTotalSteps(count)
  }, [
    shouldHaveTickets,
    shouldHaveKiosk,
    shouldHaveMerch,
    shouldHaveRestaurant,
  ])

  if (event === null || companyData === null || companyProfile === null) {
    return <LivedayLoader />
  }

  if (initialLoading) {
    return <LivedayLoader />
  }

  return (
    <>
      <Head>
        <title>{t('liveday-modern-ticket-sales')}</title>
      </Head>
      {(event?.banner?.length ?? 0) > 0 && (
        <div className="w-full bg-gray-900 px-6 py-2.5 sm:px-3.5">
          <p className="text-center text-sm leading-6 text-white">
            {event?.banner}
          </p>
        </div>
      )}
      <VenueCustomerLayout
        user={user}
        event={event}
        arena={arena}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        shouldHaveTickets={shouldHaveTickets()}
        shouldHaveItems={shouldHaveMerch()}
        shouldHaveKiosk={shouldHaveKiosk()}
        shouldHaveRestaurant={shouldHaveRestaurant()}
        shouldHaveCart={shouldHaveCart}
        refetchCart={() => fetchCartData()}
        setShowSuccessMessage={setShowSuccessMessage}
        setShowFailureMessage={setShowFailureMessage}
        cart={cart}
        ticketsInCart={ticketsInCart}
        companyProfile={companyProfile}
        refetchTickets={fetchTickets}
        organizationId={event.organization}
      >
        <SuccessNotification
          shouldShow={showSuccessMessage.length > 0}
          message={showSuccessMessage}
          subtitle={''}
          hide={() => setShowSuccessMessage('')}
        />
        <FailureNotification
          shouldShow={showFailureMessage.length > 0}
          message={t('failure')}
          subtitle={showFailureMessage}
          hide={() => setShowFailureMessage('')}
        />
        <div className={`bg-gray-50 sm:min-h-screen`}>
          {currentStep === 1 && (
            <>
              {shouldHaveTickets() ? (
                <Arena
                  event={event}
                  sections={sections}
                  setCurrentStep={setCurrentStep}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setShowFailureMessage={setShowFailureMessage}
                  refetchCart={() => fetchCartData()}
                  tickets={tickets}
                  selectedSection={selectedSection}
                  setSelectedSection={setSelectedSection}
                  arena={arena}
                  ticketsInCart={ticketsInCart}
                  fetchTickets={fetchTickets}
                  sectionLoading={sectionLoading}
                  companyProfile={companyProfile}
                  currency={organization?.currency ?? Currency.SEK}
                />
              ) : (
                <Items
                  event={event}
                  setCurrentStep={setCurrentStep}
                  currentStep={currentStep}
                  items={items}
                  refetchCart={() => fetchCartData()}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setShowFailureMessage={setShowFailureMessage}
                  shouldHaveTickets={shouldHaveTickets()}
                  shouldHaveItems={shouldHaveMerch()}
                  shouldHaveKiosk={shouldHaveKiosk()}
                  shouldHaveRestaurant={shouldHaveRestaurant()}
                  companyProfile={companyProfile}
                />
              )}
            </>
          )}
          {currentStep === 2 && (
            <>
              {currentStep < totalSteps ? (
                <Items
                  event={event}
                  setCurrentStep={setCurrentStep}
                  currentStep={currentStep}
                  items={items}
                  refetchCart={() => fetchCartData()}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setShowFailureMessage={setShowFailureMessage}
                  shouldHaveTickets={shouldHaveTickets()}
                  shouldHaveItems={shouldHaveMerch()}
                  shouldHaveKiosk={shouldHaveKiosk()}
                  shouldHaveRestaurant={shouldHaveRestaurant()}
                  companyProfile={companyProfile}
                />
              ) : (
                <CheckoutCart
                  user={user}
                  eventSk={event.sk}
                  organization={event.organization}
                  items={cart?.items ?? []}
                  campaigns={cart?.campaigns ?? []}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setShowFailureMessage={setShowFailureMessage}
                  companyProfile={companyProfile}
                  companyData={companyData}
                  setCurrentStep={setCurrentStep}
                  refetchCart={() => fetchCartData()}
                  name={name?.toString() ?? undefined}
                  email={email?.toString() ?? undefined}
                  phoneNumber={phone_number?.toString() ?? undefined}
                />
              )}
            </>
          )}
          {currentStep === 3 && (
            <>
              {currentStep < totalSteps ? (
                <Items
                  event={event}
                  setCurrentStep={setCurrentStep}
                  items={items}
                  currentStep={currentStep}
                  refetchCart={() => fetchCartData()}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setShowFailureMessage={setShowFailureMessage}
                  shouldHaveTickets={shouldHaveTickets()}
                  shouldHaveItems={shouldHaveMerch()}
                  shouldHaveKiosk={shouldHaveKiosk()}
                  shouldHaveRestaurant={shouldHaveRestaurant()}
                  companyProfile={companyProfile}
                />
              ) : (
                <CheckoutCart
                  user={user}
                  items={cart?.items ?? []}
                  campaigns={cart?.campaigns ?? []}
                  eventSk={event.sk}
                  organization={event.organization}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setShowFailureMessage={setShowFailureMessage}
                  companyProfile={companyProfile}
                  companyData={companyData}
                  setCurrentStep={setCurrentStep}
                  refetchCart={() => fetchCartData()}
                  name={name?.toString() ?? undefined}
                  email={email?.toString() ?? undefined}
                  phoneNumber={phone_number?.toString() ?? undefined}
                />
              )}
            </>
          )}
          {currentStep === 4 && (
            <>
              {currentStep < totalSteps ? (
                <Items
                  event={event}
                  setCurrentStep={setCurrentStep}
                  items={items}
                  currentStep={currentStep}
                  refetchCart={() => fetchCartData()}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setShowFailureMessage={setShowFailureMessage}
                  shouldHaveTickets={shouldHaveTickets()}
                  shouldHaveItems={shouldHaveMerch()}
                  shouldHaveKiosk={shouldHaveKiosk()}
                  shouldHaveRestaurant={shouldHaveRestaurant()}
                  companyProfile={companyProfile}
                />
              ) : (
                <CheckoutCart
                  items={cart?.items ?? []}
                  campaigns={cart?.campaigns ?? []}
                  user={user}
                  eventSk={event.sk}
                  organization={event.organization}
                  setShowSuccessMessage={setShowSuccessMessage}
                  setShowFailureMessage={setShowFailureMessage}
                  companyProfile={companyProfile}
                  companyData={companyData}
                  setCurrentStep={setCurrentStep}
                  refetchCart={() => fetchCartData()}
                  name={name?.toString() ?? undefined}
                  email={email?.toString() ?? undefined}
                  phoneNumber={phone_number?.toString() ?? undefined}
                />
              )}
            </>
          )}
          {currentStep === 5 && (
            <CheckoutCart
              items={cart?.items ?? []}
              campaigns={cart?.campaigns ?? []}
              user={user}
              eventSk={event.sk}
              organization={event.organization}
              setShowSuccessMessage={setShowSuccessMessage}
              setShowFailureMessage={setShowFailureMessage}
              companyProfile={companyProfile}
              companyData={companyData}
              setCurrentStep={setCurrentStep}
              refetchCart={() => fetchCartData()}
              name={name?.toString() ?? undefined}
              email={email?.toString() ?? undefined}
              phoneNumber={phone_number?.toString() ?? undefined}
            />
          )}
        </div>
      </VenueCustomerLayout>
    </>
  )
}
